import React, { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { ButtonV2Props } from '@withjoy/joykit';
import { AccommodationListingDTO, SortOption } from '@apps/accommodations/AccommodationsConsole.types';
import { createContext } from '@shared/utils/createContext';

export type AccommodationsConsoleConfig = Readonly<{
  topBar: {
    searchForm: {
      dateRangeEnabled: boolean;
      occupancySelectorEnabled: boolean;
    };
    backButton?: {
      label?: string;
    };
  };
  filtersPanel: {
    hideEventNameAndNote: boolean;
    shouldShowJoyTravelBenefitsFilter: boolean;
  };
  listingsPanel: {
    listingData: {
      shouldShowAllAccommodations: boolean;
      shouldFilterOutNonJoyRoomBlocks: boolean;
    };
    sortConfig: {
      sortOptions: [SortOption, string][];
      defaultSortOption: SortOption;
    };
    listingEntry: {
      roomBlockTag: string;
      shouldShowCouplePicksTag: boolean;
      shouldShowAddedTag: boolean;
      shouldShowRoomBlockNoteAndDate: boolean;
      bookingCta: {
        default: {
          buttonTitle: string;
          buttonProps: ButtonV2Props;
        };
        couplePicks?: {
          buttonTitle: string;
          buttonProps: ButtonV2Props;
        };
        onButtonClick?: (hotel: AccommodationListingDTO) => void;
      };
    };
  };
}>;

type AccommodationsConsoleConfigProviderProps = Readonly<{
  config?: AccommodationsConsoleConfig;
  children: React.ReactNode;
}>;

const [Provider, useAccommodationsConsoleConfig] = createContext<AccommodationsConsoleConfig>({
  name: 'AccommodationsConsoleConfig',
  strict: true,
  errorMessage: 'useAccommodationsConsoleConfig must be used within AccommodationsConsoleConfigProvider',
});

const AccommodationsConsoleConfigProvider = ({ config, children }: AccommodationsConsoleConfigProviderProps) => {
  const { t } = useTranslation('accommodationsConsole');
  const listingPanelTrans = t('listingsPanel');
  const sortTrans = t('sort');
  const contextValue = useMemo(() => {
    const defaultConfig: AccommodationsConsoleConfig = {
      topBar: {
        searchForm: {
          dateRangeEnabled: true,
          occupancySelectorEnabled: true,
        },
      },
      filtersPanel: {
        hideEventNameAndNote: false,
        shouldShowJoyTravelBenefitsFilter: false,
      },
      listingsPanel: {
        listingData: {
          shouldShowAllAccommodations: true,
          shouldFilterOutNonJoyRoomBlocks: false,
        },
        sortConfig: {
          sortOptions: [
            [SortOption.COUPLES_PICKS, sortTrans.couplesPicks()],
            [SortOption.PRICE_LOW_TO_HIGH, sortTrans.priceLowToHigh()],
            [SortOption.PRICE_HIGH_TO_LOW, sortTrans.priceHighToLow()],
            [SortOption.MOST_SAVINGS, sortTrans.mostSavings()],
            [SortOption.DISTANCE, sortTrans.distance()],
          ],
          defaultSortOption: SortOption.COUPLES_PICKS,
        },
        listingEntry: {
          roomBlockTag: '',
          shouldShowCouplePicksTag: true,
          shouldShowAddedTag: false,
          shouldShowRoomBlockNoteAndDate: true,
          bookingCta: {
            default: {
              buttonTitle: listingPanelTrans.hotelCTA.hotelButtonText(),
              buttonProps: {
                variant: 'solid',
                intent: 'newPrimary',
                _hover: { backgroundColor: { _: 'newUIBrandPrimary', md: 'buttonNewPrimaryHover' } },
              },
            },
          },
        },
      },
    };

    return config || defaultConfig;
  }, [config, listingPanelTrans.hotelCTA, sortTrans]);

  return <Provider value={contextValue}>{children}</Provider>;
};

export { AccommodationsConsoleConfigProvider, useAccommodationsConsoleConfig };
